import { SxProps } from "@mui/material";
import { sicCodes } from "encamp-shared/src/constants/sic";
import { gql } from "generated-graphql";
import {
  DynamicFieldFragment,
  DynamicFieldType,
} from "generated-graphql/graphql";
import { Control, FieldValues, Path, Controller } from "react-hook-form";
import { CheckboxField } from "./CheckboxField";
import { DateField } from "./DateField";
import { FormAutocomplete } from "./FormAutocomplete";
import { FormMultiselect } from "./FormMultiselect";
import { FormMultiselectWithOther } from "./FormMultiselectWithOther";
import { FormSelect } from "./FormSelect";
import { FormSelectWithOther } from "./FormSelectWithOther";
import { FormTextField } from "./FormTextField";
import { RadioGroupField } from "./RadioGroupField";
import { DateTime } from "luxon";
import { PoliceDepartmentPicker } from "../PoliceDepartmentPicker";
import { parseTooltipLinks } from "../../util/parseTooltipLinks";

gql(`
  fragment dynamicField on DynamicField {
    key
    value
    type
    jurisdiction
    label
    tooltip
    inputType
    layout
    metadata
    helperText
    group
    visibleWhen
    inputMask
    selectOptions {
      label
      value
    }
  }
`);

type Layout = {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

type DynamicFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  dynamicField: DynamicFieldFragment;
  layout?: Layout;
  sx?: SxProps;
  disabled?: boolean;
  applyDynamicLayout?: boolean;
  staticErrorSpacing?: false;
} & (
  | {
      namePrefix: string;
      name?: never;
    }
  | { namePrefix?: never; name: Path<TFieldValues> }
);

export function DynamicField<TFieldValues extends FieldValues>(
  props: DynamicFieldProps<TFieldValues>
) {
  const {
    namePrefix,
    control,
    dynamicField: {
      inputType,
      label,
      selectOptions,
      layout,
      helperText,
      metadata,
    },
    sx,
    disabled,
    applyDynamicLayout = false,
    staticErrorSpacing,
  } = props;

  const tooltip = parseTooltipLinks(props.dynamicField.tooltip ?? undefined);

  /**
   * `.value` here is the field/property name of the DynamicField object
   * ```
   *   chemicalStateField.0.key => epaId
   *   chemicalStateField.0.value => 123345
   * ```
   */
  const name = props.name ?? (`${namePrefix}.value` as Path<TFieldValues>);

  switch (inputType) {
    case DynamicFieldType.Text:
    case DynamicFieldType.Numeric:
      return (
        <FormTextField
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          helperText={helperText ?? undefined}
          InputProps={{ sx: applyDynamicLayout ? layout?.sx : undefined }}
        />
      );
    case DynamicFieldType.Textarea:
      return (
        <FormTextField
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          textFieldProps={{ multiline: true }}
          disabled={disabled}
          helperText={helperText ?? undefined}
          InputProps={{
            sx: applyDynamicLayout ? layout?.sx : undefined,
          }}
        />
      );

    case DynamicFieldType.Boolean:
      return (
        <RadioGroupField
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          radioOptions={[
            {
              label: "Yes",
              value: true,
            },
            {
              label: "No",
              value: false,
            },
          ]}
        />
      );

    case DynamicFieldType.Checkbox:
      return (
        <CheckboxField
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          staticErrorSpacing={staticErrorSpacing}
        />
      );

    case DynamicFieldType.Date:
      return (
        <DateField
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          helperText={helperText ?? undefined}
          datePickerProps={{
            maxDate: metadata?.dateNotFuture ? DateTime.now() : undefined,
          }}
        />
      );

    case DynamicFieldType.Radiogroup:
      return (
        <RadioGroupField
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          radioOptions={selectOptions ?? []}
          disabled={disabled}
        />
      );

    // Currently just rendering this as a regular select field
    // but in future we can make a new form field that is
    // responsible for fetching it's own data
    case DynamicFieldType.Select:
      return (
        <FormSelect
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          selectItems={
            selectOptions?.map((opt) => ({
              display: opt.label,
              value: opt.value,
            })) ?? []
          }
          selectProps={{
            multiple: false,
          }}
          helperText={helperText ?? undefined}
        />
      );
    case DynamicFieldType.SelectNjPoliceDepartment:
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState }) => (
            <PoliceDepartmentPicker
              label={label}
              {...fieldState}
              value={field.value ? { id: field.value } : null}
              defaultSearch={"state:NJ"}
              onChange={(policeDepartmentFragment) => {
                field.onChange(policeDepartmentFragment?.id ?? null);
              }}
              error={fieldState.error}
              disabled={disabled}
              helperText={helperText ?? undefined}
            />
          )}
        />
      );

    case DynamicFieldType.SelectWithOther:
      return (
        <FormSelectWithOther
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          selectItems={
            selectOptions?.map((opt) => ({
              display: opt.label,
              value: opt.value,
            })) ?? []
          }
          selectProps={{
            multiple: true,
          }}
          helperText={helperText ?? undefined}
        />
      );

    case DynamicFieldType.Autocomplete:
      return (
        <FormAutocomplete
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          autocompleteItems={selectOptions ?? []}
          helperText={helperText ?? undefined}
        />
      );

    case DynamicFieldType.SicCodeSelect:
      return (
        <FormAutocomplete
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          autocompleteItems={sicCodes ?? []}
          helperText={helperText ?? undefined}
          InputProps={{
            sx: applyDynamicLayout ? layout?.sx : undefined,
          }}
        />
      );

    case DynamicFieldType.Multiselect:
      return (
        <FormMultiselect
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          selectItems={
            selectOptions?.map((opt) => ({
              display: opt.label,
              value: opt.value,
            })) ?? []
          }
          selectProps={{
            multiple: true,
          }}
          helperText={helperText ?? undefined}
        />
      );

    case DynamicFieldType.MultiselectWithOther:
      return (
        <FormMultiselectWithOther
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          selectItems={
            selectOptions?.map((opt) => ({
              display: opt.label,
              value: opt.value,
            })) ?? []
          }
          selectProps={{
            multiple: true,
          }}
          helperText={helperText ?? undefined}
        />
      );

    case DynamicFieldType.PhoneNumber:
      return (
        <FormTextField
          intOnly
          sx={sx}
          control={control}
          name={name}
          label={label}
          tooltip={tooltip}
          disabled={disabled}
          helperText={helperText ?? undefined}
          inputMask={metadata?.phoneNumberInputMask}
        />
      );
  }
}
